import React, {
  ChangeEvent,
  Dispatch,
  FC,
  KeyboardEvent,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Routes from "models/routes";
import { GridToolbarContainer } from "@mui/x-data-grid";
import CommonButton from "components/common/ui/CommonButton";
import AddIcon from "@mui/icons-material/Add";
import SpacedTextInput, {
  SpacedAutocompleteInput,
} from "components/common/ui/SpacedTextInput";
import { Box, Grid, InputAdornment, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SoftSquareIconButton from "components/common/ui/SoftSquareIconButton";
import RecipeStepGridItemBox from "components/food/ui/recipe/RecipeStepGridItemBox";
import ClearIcon from "@mui/icons-material/Clear";
import CircleIconButton from "components/common/ui/CircleIconButton";
import {
  FoodItemSearchingParams,
  MenuSearchingParams,
} from "client/jspPlatformExperiment";
import { useAppSelector } from "hooks/useReduxStore";
import userGroupOptionsSelector from "store/selectors/userGroup/userGroupOptionSelector";

interface MenuListTableToolbarProps {
  // setSearchingParams: Dispatch<SetStateAction<MenuSearchingParams>>;
  setSearchingParams: (newSearchingParams: MenuSearchingParams) => void;
}

const MenuListTableToolbar: FC<MenuListTableToolbarProps> = ({
  setSearchingParams,
}) => {
  const [searchParams] = useSearchParams();
  const siteOptionIDs = useAppSelector(userGroupOptionsSelector.siteOptionIDs);
  const userGroupOptions = useAppSelector(
    userGroupOptionsSelector.userGroupOptions,
  );
  const navigate = useNavigate();

  // Parse initial values from URL params
  const initialKeywords = useMemo(
    () => searchParams.get("keywords") || undefined,
    [searchParams],
  );
  const initialSites = useMemo(() => {
    const sitesParam = searchParams.get("sites");
    if (sitesParam && sitesParam.length > 0) {
      return sitesParam
        .split(",")
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id));
    }
    return [];
  }, [searchParams]);

  const [displayKeywords, setDisplayKeywords] = useState<string | undefined>(
    initialKeywords,
  );
  const [linkedToAnyUserGroupID, setLinkedToAnyUserGroupID] =
    useState<Array<number>>(initialSites);

  // Sync local state with URL params when they change externally
  useEffect(() => {
    setDisplayKeywords(initialKeywords);
  }, [initialKeywords]);

  useEffect(() => {
    setLinkedToAnyUserGroupID(initialSites);
  }, [initialSites]);

  /**
   * Handle the keywords change
   * @param event
   */
  const handleKeywordsChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value ?? undefined;
    setDisplayKeywords(newValue);
  };

  /**
   * Handle click on create button
   */
  const handleCreateClick = (): void => {
    navigate(Routes.MENU_CREATE, {
      replace: false,
    });
  };

  /**
   * Handle click on search button
   */
  const handleSearchClick = useCallback(
    (searchingKeywords: string | undefined) => (): void => {
      setSearchingParams({
        keywords: searchingKeywords,
        linked_user_group_ids_any: linkedToAnyUserGroupID,
      });
    },
    [linkedToAnyUserGroupID, setSearchingParams],
  );

  /**
   * Handle click on clear icon in searching bar
   */
  const handleClearClick = useCallback(() => {
    handleSearchClick(undefined)();
    setDisplayKeywords(undefined);
  }, [handleSearchClick]);

  /**
   * Handle keypress while focus on keyword field
   */
  const handleKeypress = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        handleSearchClick(displayKeywords)();
      } else if (event.key === "Escape") {
        handleClearClick();
      }
    },
    [displayKeywords, handleClearClick, handleSearchClick],
  );

  /**
   * Handle the linked user group IDs change
   */
  const handleLinkedUserGroupIDsChange = useCallback(
    (_: SyntheticEvent, newValue: Array<number>) => {
      setLinkedToAnyUserGroupID(newValue);
      setSearchingParams({
        keywords: displayKeywords,
        linked_user_group_ids_any: newValue.length > 0 ? newValue : undefined,
      });
    },
    [displayKeywords, setSearchingParams],
  );

  return (
    <GridToolbarContainer>
      <Grid container spacing={1} columns={24}>
        <Grid item xs={24} sm={24} md={7} lg={3}>
          <CommonButton
            text="Create"
            positive
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          />
        </Grid>
        <Grid item xs={24} sm={24} md={7} lg={5}>
          <RecipeStepGridItemBox>
            <SpacedAutocompleteInput
              multiple
              label="Sites (any)"
              size="small"
              options={siteOptionIDs}
              getOptionLabel={(siteID) =>
                userGroupOptions[siteID].group_name ?? `Site ID: ${siteID}`
              }
              value={linkedToAnyUserGroupID}
              onChange={handleLinkedUserGroupIDsChange}
            />
          </RecipeStepGridItemBox>
        </Grid>
        <Grid item xs={24} sm={24} md={17} lg={16}>
          <RecipeStepGridItemBox>
            <SpacedTextInput
              size="small"
              label="Keywords"
              InputProps={{
                endAdornment: displayKeywords ? (
                  <InputAdornment position="end">
                    <CircleIconButton
                      natural
                      sizepx={24}
                      onClick={handleClearClick}
                    >
                      <ClearIcon />
                    </CircleIconButton>
                  </InputAdornment>
                ) : null,
              }}
              onChange={handleKeywordsChange}
              onKeyDown={handleKeypress}
              value={displayKeywords ?? ""}
            />
            <Box>
              <Tooltip title="Search">
                <Box padding="3px 1rem 3px 0.5rem">
                  <SoftSquareIconButton
                    positive
                    sizepx={34}
                    onClick={handleSearchClick(displayKeywords)}
                  >
                    <SearchIcon />
                  </SoftSquareIconButton>
                </Box>
              </Tooltip>
            </Box>
          </RecipeStepGridItemBox>
        </Grid>
      </Grid>
    </GridToolbarContainer>
  );
};

export default MenuListTableToolbar;
